import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/AuthProvider.js";
import { Button, Modal, ProductImage } from "../../components/index.js";
import type { FbaInventoryItem } from "../../types.js";
import type { PlanItemSelectionRow } from "../../lib/plan-item-select.js";

/**
 * Picker de produtos do inventário (Design.md §6.1 "Adicionar SKU do inventário").
 * Lista o snapshot FBA (GET /api/v1/inventory), exclui o que já está selecionado,
 * e devolve as linhas escolhidas (com quantidade) para a Etapa 1.
 *
 * Quando o snapshot está vazio, o picker NÃO é um beco sem saída: o ADMIN pode
 * "Sincronizar da Amazon" (POST /inventory/sync, FBA Inventory API via task) e
 * recarregar. Sem isto o operador via "Nenhum produto disponível" sem nenhuma
 * ação — o snapshot só era populado por uma sync que não tinha gatilho na UI.
 */
export interface AdicionarSkuModalProps {
  open: boolean;
  onClose: () => void;
  marketplaceId: string;
  sellerId: string;
  existingMskus: ReadonlySet<string>;
  onAdd: (rows: PlanItemSelectionRow[]) => void;
}

interface Pick {
  checked: boolean;
  quantity: number;
}

export function AdicionarSkuModal({
  open,
  onClose,
  marketplaceId,
  sellerId,
  existingMskus,
  onAdd
}: AdicionarSkuModalProps): ReactNode {
  const { api, me } = useAuth();
  const isAdmin = me?.role === "ADMIN";
  const [inventory, setInventory] = useState<FbaInventoryItem[]>([]);
  const [picks, setPicks] = useState<Record<string, Pick>>({});
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async (): Promise<void> => {
    setCarregando(true);
    setErro(null);
    try {
      const res = await api.getInventory(marketplaceId);
      if (alive.current) setInventory(res.items);
    } catch (error: unknown) {
      if (alive.current) {
        setErro(error instanceof Error ? error.message : "Falha ao carregar o inventário.");
      }
    } finally {
      if (alive.current) setCarregando(false);
    }
  }, [api, marketplaceId]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setPicks({});
    setSyncMsg(null);
    void load();
  }, [open, load]);

  async function sincronizar(): Promise<void> {
    if (sellerId.trim().length === 0) {
      setErro(
        "Seller ID ausente no plano. Configure o Seller ID em Configurações."
      );
      return;
    }
    setSincronizando(true);
    setErro(null);
    setSyncMsg(null);
    try {
      await api.syncInventory(sellerId, marketplaceId);
      if (alive.current) {
        setSyncMsg(
          'Sincronização solicitada à Amazon (assíncrona). Aguarde alguns ' +
            'segundos e clique em "Atualizar" para carregar os produtos.'
        );
      }
    } catch (error: unknown) {
      if (alive.current) {
        setErro(
          error instanceof Error ? error.message : "Falha ao sincronizar o inventário."
        );
      }
    } finally {
      if (alive.current) setSincronizando(false);
    }
  }

  const available = inventory.filter((item) => !existingMskus.has(item.sellerSku));

  function toggle(sku: string, defaultQty: number): void {
    setPicks((prev) => {
      const current = prev[sku];
      const next = { ...prev };
      if (current?.checked === true) {
        next[sku] = { checked: false, quantity: current.quantity };
      } else {
        next[sku] = { checked: true, quantity: current?.quantity ?? defaultQty };
      }
      return next;
    });
  }

  function setQty(sku: string, quantity: number): void {
    setPicks((prev) => ({
      ...prev,
      [sku]: { checked: prev[sku]?.checked ?? true, quantity }
    }));
  }

  function adicionar(): void {
    const rows: PlanItemSelectionRow[] = available
      .filter((item) => picks[item.sellerSku]?.checked === true)
      .map((item) => ({
        msku: item.sellerSku,
        asin: item.asin,
        fnsku: item.fnSku,
        productName: item.productName,
        quantity: Math.max(1, picks[item.sellerSku]?.quantity ?? 1),
        labelOwner: "SELLER",
        // Validade em branco; o operador preenche na linha se o SKU for datado.
        expiration: ""
      }));
    onAdd(rows);
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="Adicionar SKU do inventário" width={760}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.5rem",
          marginBottom: "0.75rem",
          flexWrap: "wrap"
        }}
      >
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          Snapshot do estoque FBA · Amazon UK
        </span>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button variant="secondary" onClick={() => void load()} disabled={carregando}>
            {carregando ? "Atualizando…" : "Atualizar"}
          </Button>
          {isAdmin ? (
            <Button onClick={() => void sincronizar()} disabled={sincronizando}>
              {sincronizando ? "Sincronizando…" : "Sincronizar da Amazon"}
            </Button>
          ) : null}
        </div>
      </div>

      {erro !== null ? <p role="alert" style={{ color: "var(--orange)" }}>{erro}</p> : null}
      {syncMsg !== null ? (
        <p role="status" style={{ fontSize: 12.5, color: "var(--blue)" }}>{syncMsg}</p>
      ) : null}
      {carregando ? <p role="status">Carregando inventário…</p> : null}
      {!carregando && available.length === 0 ? (
        <p style={{ color: "var(--text-muted)" }}>
          {inventory.length === 0
            ? isAdmin
              ? 'Nenhum produto no snapshot de inventário. Clique em "Sincronizar da Amazon" e depois em "Atualizar" para carregar os produtos do FBA.'
              : "Nenhum produto no snapshot de inventário. Peça a um Admin para sincronizar o inventário da Amazon."
            : "Todos os produtos do inventário já foram selecionados."}
        </p>
      ) : null}

      {available.length > 0 ? (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={th} />
              <th style={th}>Produto</th>
              <th style={{ ...th, textAlign: "right" }}>Em estoque</th>
              <th style={{ ...th, textAlign: "right" }}>Quantidade</th>
            </tr>
          </thead>
          <tbody>
            {available.map((item) => {
              const pick = picks[item.sellerSku];
              const checked = pick?.checked === true;
              return (
                <tr key={item.id} className="ficc-hover-row">
                  <td style={td}>
                    <input
                      type="checkbox"
                      aria-label={`Incluir ${item.sellerSku}`}
                      checked={checked}
                      onChange={() => toggle(item.sellerSku, item.fulfillableQuantity)}
                    />
                  </td>
                  <td style={td}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                      <ProductImage
                        src={item.imageUrl}
                        label={item.productName || item.sellerSku}
                        size={34}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>
                          {item.productName || item.sellerSku}
                        </div>
                        <div className="mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>
                          {item.sellerSku} · {item.asin}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>{item.fulfillableQuantity}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <input
                      type="number"
                      min={1}
                      disabled={!checked}
                      value={pick?.quantity ?? item.fulfillableQuantity}
                      onChange={(e) => setQty(item.sellerSku, Math.max(1, Number(e.target.value) || 1))}
                      aria-label={`Quantidade de ${item.sellerSku}`}
                      style={{ width: 90 }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1rem" }}>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={adicionar}>Adicionar selecionados</Button>
      </div>
    </Modal>
  );
}

const th = {
  textAlign: "left",
  background: "var(--panel-alt)",
  color: "var(--text-muted)",
  fontSize: 10.5,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: "0.55rem 0.7rem",
  borderBottom: "1px solid var(--border)"
} as const;

const td = {
  padding: "0.55rem 0.7rem",
  borderBottom: "1px solid var(--border-soft)",
  verticalAlign: "middle"
} as const;
